import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';

// Core Skills Interfaces - Enhanced for Phase 3
export interface Skill {
  id: number;
  skill_code: string;
  skill_name: string;
  description?: string;
  category: string;
  subcategory?: string;
  parent_skill_id?: number;
  skill_type: 'capability' | 'certification' | 'equipment' | 'restriction' | 'preference';
  requires_certification: boolean;
  certification_authority?: string;
  certification_expiry_required: boolean;
  vroom_skill_id?: number;
  project_id?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Computed fields for skill usage statistics
  vehicle_count?: number;
  job_count?: number;
  shipment_count?: number;
  total_usage?: number;
  usage_count?: number;
}

export interface SkillCreate {
  skill_code: string;
  skill_name: string;
  description?: string;
  category: string;
  subcategory?: string;
  parent_skill_id?: number;
  skill_type: 'capability' | 'certification' | 'equipment' | 'restriction' | 'preference';
  requires_certification: boolean;
  certification_authority?: string;
  certification_expiry_required: boolean;
  vroom_skill_id?: number;
}

export interface SkillUpdate {
  skill_name?: string;
  description?: string;
  category?: string;
  subcategory?: string;
  parent_skill_id?: number;
  skill_type?: 'capability' | 'certification' | 'equipment' | 'restriction' | 'preference';
  requires_certification?: boolean;
  certification_authority?: string;
  certification_expiry_required?: boolean;
  vroom_skill_id?: number;
  is_active?: boolean;
}

// Junction Table Interfaces
export interface VehicleSkill {
  vehicle_id: number;
  skill_id: number;
  created_at: string;
  updated_at: string;
  skill?: Skill;
}

export interface JobSkill {
  job_id: number;
  skill_id: number;
  created_at: string;
  updated_at: string;
  skill?: Skill;
}

export interface ShipmentSkill {
  shipment_id: number;
  skill_id: number;
  created_at: string;
  updated_at: string;
  skill?: Skill;
}

// Bulk Operations Interfaces
export interface BulkSkillAssignment {
  skill_ids: number[];
  entity_type: 'vehicle' | 'job' | 'shipment';
  entity_ids: number[];
}

export interface SkillConflict {
  skill_id: number;
  skill_name: string;
  conflict_type: 'required_but_missing' | 'exclusive_conflict' | 'capacity_conflict';
  affected_entities: {
    type: 'vehicle' | 'job' | 'shipment';
    id: number;
    name: string;
  }[];
  severity: 'warning' | 'error';
  message: string;
}

export interface SkillUsageStats {
  skill_id: number;
  skill_name: string;
  vehicles_using: number;
  jobs_requiring: number;
  shipments_requiring: number;
  total_usage: number;
  last_used: string | null;
}

// Core Skills CRUD Hooks

// Hook to get all skills with proper error handling and edge cases
export const useSkills = (projectId: number, options?: {
  search?: string;
  category?: string;
  skill_type?: string;
  include_global?: boolean;
  is_active?: boolean;
}) => {
  return useQuery<Skill[], Error>({
    queryKey: ['skills', projectId, options],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          project_id: projectId.toString(),
          include_global: (options?.include_global ?? true).toString(),
          ...(options?.category && { category: options.category }),
          ...(options?.skill_type && { skill_type: options.skill_type }),
          ...(options?.is_active !== undefined && { is_active: options.is_active.toString() }),
        });

        // Use APIClient's callEdgeFunction method for proper CORS handling
        const response = await (apiClient as any).callEdgeFunction(`skills?${params}`);
        
        // Extract data from the structured response
        const skills = response?.data || response || [];
        
        console.log('Skills fetched from Edge Function:', skills);
        
        // Return skills without usage counts for now (can be added later via separate API)
        return Array.isArray(skills) ? skills : [];
      } catch (error) {
        console.error('Error fetching skills:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 3;
    },
  });
};

// Hook to get a single skill
export const useSkill = (skillId: number, options?: { include_usage?: boolean }) => {
  return useQuery({
    queryKey: ['skill', skillId, options],
    queryFn: () => {
      // TODO: Implement getSkill in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve({ id: skillId, name: 'Placeholder', description: '', created_at: '', updated_at: '' });
    },
    enabled: !!skillId,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook to create a skill with comprehensive error handling
export const useCreateSkill = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Skill, Error, SkillCreate>({
    mutationFn: async (skill: SkillCreate) => {
      try {
        const response = await fetch('/api/skills/taxonomy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify(skill)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 409) throw new Error('Skill code already exists');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Validation error: Please check your input');
          }
          throw new Error(errorData?.detail || `Failed to create skill: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to create skill');
      }
    },
    onSuccess: (data) => {
      // Invalidate skills list to refetch with new skill
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      // Add the new skill to the cache
      queryClient.setQueryData(['skill', data.id], data);
      // Invalidate skill usage statistics
      queryClient.invalidateQueries({ queryKey: ['skill-usage-stats'] });
    },
    onError: (error) => {
      console.error('Error creating skill:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to update a skill with conflict resolution
export const useUpdateSkill = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Skill, Error, { id: number; updates: SkillUpdate }>({
    mutationFn: async ({ id, updates }) => {
      try {
        const response = await fetch(`/api/skills/taxonomy/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify(updates)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Skill not found');
          if (response.status === 409) throw new Error('Skill update conflicts with existing data');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Validation error: Please check your input');
          }
          throw new Error(errorData?.detail || `Failed to update skill: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to update skill');
      }
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['skill', id] });
      await queryClient.cancelQueries({ queryKey: ['skills'] });
      
      // Snapshot the previous value
      const previousSkill = queryClient.getQueryData(['skill', id]);
      
      return { previousSkill };
    },
    onSuccess: (data, variables) => {
      // Update the skill in cache
      queryClient.setQueryData(['skill', variables.id], data);
      // Invalidate skills list
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      // Invalidate any entity that might use this skill
      queryClient.invalidateQueries({ queryKey: ['vehicle-skills'] });
      queryClient.invalidateQueries({ queryKey: ['job-skills'] });
      queryClient.invalidateQueries({ queryKey: ['shipment-skills'] });
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSkill) {
        queryClient.setQueryData(['skill', variables.id], context.previousSkill);
      }
      console.error('Error updating skill:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to delete a skill with usage validation
export const useDeleteSkill = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, number>({
    mutationFn: async (skillId: number) => {
      try {
        // First check if skill is in use
        const usageResponse = await fetch(`/api/skills/taxonomy/${skillId}/usage`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (usageResponse.ok) {
          const usage = await usageResponse.json();
          if (usage.total_usage > 0) {
            throw new Error(`Cannot delete skill: Used by ${usage.vehicle_count} vehicles, ${usage.job_count} jobs, and ${usage.shipment_count} shipments`);
          }
        }
        
        // Proceed with deletion
        const response = await fetch(`/api/skills/taxonomy/${skillId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Skill not found');
          if (response.status === 409) throw new Error('Cannot delete skill: It is currently in use');
          throw new Error(errorData?.detail || `Failed to delete skill: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to delete skill');
      }
    },
    onMutate: async (skillId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['skill', skillId] });
      await queryClient.cancelQueries({ queryKey: ['skills'] });
    },
    onSuccess: (_, skillId) => {
      // Remove skill from cache
      queryClient.removeQueries({ queryKey: ['skill', skillId] });
      // Invalidate skills list
      queryClient.invalidateQueries({ queryKey: ['skills'] });
      // Invalidate any entity that might use this skill
      queryClient.invalidateQueries({ queryKey: ['vehicle-skills'] });
      queryClient.invalidateQueries({ queryKey: ['job-skills'] });
      queryClient.invalidateQueries({ queryKey: ['shipment-skills'] });
      // Invalidate usage statistics
      queryClient.invalidateQueries({ queryKey: ['skill-usage-stats'] });
    },
    onError: (error) => {
      console.error('Error deleting skill:', error);
    },
    retry: false, // Never retry delete operations
  });
};

// Vehicle Skills Junction Table Hooks

// Hook to get skills for a vehicle
export const useVehicleSkills = (vehicleId: number) => {
  return useQuery<VehicleSkill[], Error>({
    queryKey: ['vehicle-skills', vehicleId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/vehicles/${vehicleId}/skills`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Vehicle not found');
          throw new Error(`Failed to fetch vehicle skills: ${response.status}`);
        }
        
        const vehicleSkills = await response.json();
        
        // Fetch full skill details for each skill
        const skillsWithDetails = await Promise.all(
          vehicleSkills.map(async (vs: VehicleSkill) => {
            try {
              const skillResponse = await fetch(`/api/skills/taxonomy/${vs.skill_id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
                }
              });
              if (skillResponse.ok) {
                vs.skill = await skillResponse.json();
              }
            } catch {
              // If skill details fail, continue without them
            }
            return vs;
          })
        );
        
        return skillsWithDetails;
      } catch (error) {
        console.error('Error fetching vehicle skills:', error);
        throw error;
      }
    },
    enabled: !!vehicleId && vehicleId > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to assign skills to a vehicle with proficiency levels
export const useAssignVehicleSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { vehicleId: number; skillIds: number[]; proficiencyLevels?: Record<number, number> }>({
    mutationFn: async ({ vehicleId, skillIds, proficiencyLevels = {} }) => {
      try {
        // Prepare skill assignments with proficiency levels
        const assignments = skillIds.map(skillId => ({
          skill_id: skillId,
          proficiency_level: proficiencyLevels[skillId] || 5, // Default to Competent (5)
          verified: true,
          verification_date: new Date().toISOString()
        }));
        
        const response = await fetch(`/api/vehicles/${vehicleId}/skills`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify({ skills: assignments })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Vehicle not found');
          if (response.status === 409) throw new Error('Skill assignment conflict: Check for duplicates');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Invalid skill assignment data');
          }
          throw new Error(errorData?.detail || `Failed to assign skills: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to assign skills');
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate vehicle skills
      queryClient.invalidateQueries({ queryKey: ['vehicle-skills', variables.vehicleId] });
      // Invalidate vehicle data that includes skills
      queryClient.invalidateQueries({ queryKey: ['vehicle', variables.vehicleId] });
      // Invalidate skill usage statistics
      queryClient.invalidateQueries({ queryKey: ['skill-usage-stats'] });
      // Invalidate skills list to update usage counts
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error) => {
      console.error('Error assigning vehicle skills:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to remove skills from a vehicle
export const useRemoveVehicleSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { vehicleId: number; skillIds: number[] }>({
    mutationFn: async ({ vehicleId, skillIds }) => {
      try {
        const response = await fetch(`/api/vehicles/${vehicleId}/skills`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify({ skill_ids: skillIds })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Vehicle or skills not found');
          throw new Error(errorData?.detail || `Failed to remove skills: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to remove skills');
      }
    },
    onMutate: async ({ vehicleId, skillIds }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['vehicle-skills', vehicleId] });
      
      // Optimistically update the cache
      const previousSkills = queryClient.getQueryData(['vehicle-skills', vehicleId]);
      if (previousSkills) {
        queryClient.setQueryData(['vehicle-skills', vehicleId], 
          (old: VehicleSkill[]) => old.filter(vs => !skillIds.includes(vs.skill_id))
        );
      }
      
      return { previousSkills };
    },
    onSuccess: (_, variables) => {
      // Invalidate vehicle skills
      queryClient.invalidateQueries({ queryKey: ['vehicle-skills', variables.vehicleId] });
      // Invalidate vehicle data that includes skills
      queryClient.invalidateQueries({ queryKey: ['vehicle', variables.vehicleId] });
      // Invalidate skill usage statistics
      queryClient.invalidateQueries({ queryKey: ['skill-usage-stats'] });
      // Invalidate skills list to update usage counts
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousSkills) {
        queryClient.setQueryData(['vehicle-skills', variables.vehicleId], context.previousSkills);
      }
      console.error('Error removing vehicle skills:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Job Skills Junction Table Hooks

// Hook to get skills for a job
export const useJobSkills = (jobId: number) => {
  return useQuery<JobSkill[], Error>({
    queryKey: ['job-skills', jobId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/jobs/${jobId}/skills`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Job not found');
          throw new Error(`Failed to fetch job skills: ${response.status}`);
        }
        
        const jobSkills = await response.json();
        
        // Fetch full skill details for each skill
        const skillsWithDetails = await Promise.all(
          jobSkills.map(async (js: JobSkill) => {
            try {
              const skillResponse = await fetch(`/api/skills/taxonomy/${js.skill_id}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
                }
              });
              if (skillResponse.ok) {
                js.skill = await skillResponse.json();
              }
            } catch {
              // If skill details fail, continue without them
            }
            return js;
          })
        );
        
        return skillsWithDetails;
      } catch (error) {
        console.error('Error fetching job skills:', error);
        throw error;
      }
    },
    enabled: !!jobId && jobId > 0,
    staleTime: 2 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to assign skills to a job
export const useAssignJobSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { jobId: number; skillIds: number[]; required?: boolean }>({
    mutationFn: async ({ jobId, skillIds, required = true }) => {
      try {
        const assignments = skillIds.map(skillId => ({
          skill_id: skillId,
          required: required
        }));
        
        const response = await fetch(`/api/jobs/${jobId}/skills`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify({ skills: assignments })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Job not found');
          if (response.status === 409) throw new Error('Skill requirement conflict: Check for duplicates');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Invalid skill requirement data');
          }
          throw new Error(errorData?.detail || `Failed to assign job skills: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to assign job skills');
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate job skills
      queryClient.invalidateQueries({ queryKey: ['job-skills', variables.jobId] });
      // Invalidate job data that includes skills
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
      // Invalidate skill usage statistics
      queryClient.invalidateQueries({ queryKey: ['skill-usage-stats'] });
      // Invalidate skills list to update usage counts
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
    onError: (error) => {
      console.error('Error assigning job skills:', error);
    },
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// Hook to remove skills from a job
export const useRemoveJobSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ jobId, skillIds }: { jobId: number; skillIds: number[] }) => {
      // TODO: Implement removeJobSkills in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve();
    },
    onSuccess: (_, variables) => {
      // Invalidate job skills
      queryClient.invalidateQueries({ queryKey: ['job-skills', variables.jobId] });
      // Invalidate job data that includes skills
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
    },
  });
};

// Shipment Skills Junction Table Hooks

// Hook to get skills for a shipment
export const useShipmentSkills = (shipmentId: number) => {
  return useQuery({
    queryKey: ['shipment-skills', shipmentId],
    queryFn: () => {
      // TODO: Implement getShipmentSkills in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve([]);
    },
    enabled: !!shipmentId,
    staleTime: 2 * 60 * 1000,
  });
};

// Hook to assign skills to a shipment
export const useAssignShipmentSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ shipmentId, skillIds }: { shipmentId: number; skillIds: number[] }) => {
      // TODO: Implement assignShipmentSkills in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve();
    },
    onSuccess: (_, variables) => {
      // Invalidate shipment skills
      queryClient.invalidateQueries({ queryKey: ['shipment-skills', variables.shipmentId] });
      // Invalidate shipment data that includes skills
      queryClient.invalidateQueries({ queryKey: ['shipment', variables.shipmentId] });
    },
  });
};

// Hook to remove skills from a shipment
export const useRemoveShipmentSkills = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ shipmentId, skillIds }: { shipmentId: number; skillIds: number[] }) => {
      // TODO: Implement removeShipmentSkills in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve();
    },
    onSuccess: (_, variables) => {
      // Invalidate shipment skills
      queryClient.invalidateQueries({ queryKey: ['shipment-skills', variables.shipmentId] });
      // Invalidate shipment data that includes skills
      queryClient.invalidateQueries({ queryKey: ['shipment', variables.shipmentId] });
    },
  });
};

// Bulk Operations Hooks

// Hook for bulk skill assignment
export const useBulkSkillAssignment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (assignment: BulkSkillAssignment) => {
      // TODO: Implement bulkAssignSkills in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve();
    },
    onSuccess: (_, variables) => {
      // Invalidate all relevant caches based on entity type
      const entityType = variables.entity_type;
      variables.entity_ids.forEach(entityId => {
        queryClient.invalidateQueries({ queryKey: [`${entityType}-skills`, entityId] });
        queryClient.invalidateQueries({ queryKey: [entityType, entityId] });
      });
      
      // Invalidate skills usage statistics
      queryClient.invalidateQueries({ queryKey: ['skills'] });
    },
  });
};

// Validation and Analysis Hooks

// Hook to validate skill assignments and detect conflicts
export const useSkillConflictAnalysis = (projectId: number) => {
  return useQuery({
    queryKey: ['skill-conflicts', projectId],
    queryFn: () => {
      // TODO: Implement analyzeSkillConflicts in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve([]);
    },
    enabled: !!projectId,
    staleTime: 1 * 60 * 1000, // 1 minute (conflicts change frequently)
  });
};

// Hook to get skill usage statistics
export const useSkillUsageStats = (projectId?: number) => {
  return useQuery({
    queryKey: ['skill-usage-stats', projectId],
    queryFn: () => {
      // TODO: Implement getSkillUsageStats in apiClient
      console.warn('Skills API not implemented yet');
      return Promise.resolve([]);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Utility function for skill validation
export const validateSkillAssignment = (
  vehicleSkills: number[],
  requiredSkills: number[]
): { valid: boolean; missingSkills: number[] } => {
  const missingSkills = requiredSkills.filter(
    skillId => !vehicleSkills.includes(skillId)
  );
  
  return {
    valid: missingSkills.length === 0,
    missingSkills
  };
};

// Utility function to get skill names from IDs
export const getSkillNames = (skillIds: number[], skills: Skill[]): string[] => {
  return skillIds.map(id => {
    const skill = skills.find(s => s.id === id);
    return skill ? skill.skill_name : `Unknown Skill (${id})`;
  });
};
